/**
 * -------- Helpers for Scroll-to-selected --------
 * 
 * These functions handle scrolling to a selected row in the table.
 * Works with both normal and virtual scrolling modes in Ant Design v5.
 */

/**
 * Gets the scrollable element within the table container.
 * In AntD v5: .ant-table-tbody is the scroll container;
 * in virtual mode it's .ant-table-tbody-virtual-holder
 */
function getTableScrollElement(container: HTMLElement): HTMLElement | null {
  const body = container.querySelector<HTMLElement>(".ant-table-tbody");
  if (!body) return null;
  const holder = body.querySelector<HTMLElement>(".ant-table-tbody-virtual-holder");
  return holder ?? body;
}

/**
 * Safely escapes a string for use in CSS selectors.
 * Uses native CSS.escape if available, otherwise falls back to manual escaping.
 */
function cssEscapeSafe(value: string): string {
  const cssObj = (window as unknown as { CSS?: { escape?: (s: string) => string } }).CSS;

  if (cssObj && typeof cssObj.escape === "function") {
    return cssObj.escape(value);
  }

  // Default fallback escaping for quotes and backslashes
  return value.replace(/["'\\]/g, "\\$&");
}

/**
 * Finds a table row element by its row key or index.
 * Tries key first, then falls back to index if key is not provided or not found.
 * Uses different selectors for virtual vs normal scrolling modes.
 * 
 * @param scroller - The scrollable container element
 * @param key - Optional row key to search for
 * @param index - Optional row index to search for (fallback)
 * @param virtual - Whether virtual scrolling is enabled
 * @returns The found row element, or null if not found
 */
function findRowElement(
  scroller: HTMLElement,
  key: React.Key | null,
  index: number | null,
  virtual: boolean
): HTMLElement | null {
  // Try to find by key first
  if (key !== null) {
    const sel = cssEscapeSafe(String(key));
    const selector = virtual
        ? `.ant-table-row[data-row-key="${sel}"]`
        : `tbody > tr[data-row-key="${sel}"]`;
    const rowEl = scroller.querySelector<HTMLElement>(selector);
    if (rowEl) return rowEl;
  }

  // Fall back to index if key not found or not provided
  if (index !== null && index >= 0) {
    if (virtual) {
      const rows = Array.from(scroller.querySelectorAll<HTMLElement>(".ant-table-row[data-row-key]"));
      return rows[index] ?? null;
    } else {
      const rows = Array.from(scroller.querySelectorAll<HTMLElement>("tbody > tr[data-row-key]"));
      return rows[index] ?? null;
    }
  }

  return null;
}

/**
 * Scrolls a row element into view within the scrollable container.
 * Only scrolls if the row is outside the visible area.
 */
function scrollRowIntoView(scroller: HTMLElement, rowEl: HTMLElement) {
  const rowRect = rowEl.getBoundingClientRect();
  const scRect = scroller.getBoundingClientRect();

  // Calculate absolute positions within the scroll container
  const targetTop = rowRect.top - scRect.top + scroller.scrollTop;
  const targetBottom = rowRect.bottom - scRect.top + scroller.scrollTop;

  // Get current visible area
  const visibleTop = scroller.scrollTop;
  const visibleBottom = scroller.scrollTop + scroller.clientHeight;

  // Scroll up if row is above visible area
  if (targetTop < visibleTop) {
    scroller.scrollTop = targetTop;
  } 
  // Scroll down if row is below visible area
  else if (targetBottom > visibleBottom) {
    scroller.scrollTop = targetBottom - scroller.clientHeight;
  }
}

/**
 * Entry point for scroll-to-selected functionality.
 * Handles scrolling to a selected row by key or index.
 * Works with both normal and virtual scrolling modes.
 * 
 * Strategy:
 * 1. Try to find the row element by key or index
 * 2. If found, scroll it into view
 * 3. If in virtual mode and row not yet rendered, estimate position and scroll,
 *    then wait for render and scroll to precise position
 */
function handleScrollTo(
    container: HTMLDivElement,
    selectedRowKey: React.Key | null,
    selectedIndex: number | null,
    enableVirtual: boolean,
) {
  const scroller = getTableScrollElement(container);
  if (!scroller) return;

  if (!selectedRowKey && (!selectedIndex || selectedIndex < 0)) return;

  // 1) Try to find the selected row element
  const rowEl = findRowElement(scroller, selectedRowKey, selectedIndex, enableVirtual);
  if (rowEl) {
    requestAnimationFrame(() => scrollRowIntoView(scroller, rowEl));
  }

  // 2) Virtual mode: if row not rendered yet, use heuristic calculation
  else if (enableVirtual && !rowEl && selectedIndex && selectedIndex >= 0) {
    // Estimate scroll position based on average row height
    const sampleRow = scroller.querySelector<HTMLElement>(".ant-table-row[data-row-key]");
    const estimatedRowHeight = sampleRow?.getBoundingClientRect().height ?? 40;
    scroller.scrollTop = Math.max(0, selectedIndex * estimatedRowHeight);

    // Wait 2 frames for virtual scrolling to render, then scroll to precise position
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const rowElement = findRowElement(scroller, selectedRowKey, selectedIndex, true);
        if (rowElement) scrollRowIntoView(scroller, rowElement);
      });
    });
    return;
  }
}

export {handleScrollTo};
